import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Alert, Box, Button, Stack, Typography } from '@mui/material'

import { useGetBlueprintFamilyEventGroupDurationDistributionQuery } from '../../api/blueprintFamilies/durationDistributionEventGroups'
import { useGetBlueprintFamilyMomentumQuery } from '../../api/blueprintFamilies/momentum'
import { useGetBlueprintFamilyRollupQuery } from '../../api/blueprintFamilies/rollup'
import { useGetBlueprintFamilyEventGroupTrendsQuery } from '../../api/blueprintFamilies/trendsEventGroups'
import { useGetBlueprintFamiliesQuery } from '../../api/core'
import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import { BlueprintHierarchyBar, BlueprintHierarchyMenus } from '../../features/market-analytics/components/BlueprintHierarchyBar/BlueprintHierarchyBar'
import { DetailPageMessageCard } from '../../features/market-analytics/components/DetailPageMessageCard/DetailPageMessageCard'
import { EventCategoryTypeRow, EventCategoryTypesCard } from '../../features/market-analytics/components/EventCategoryTypesCard/EventCategoryTypesCard'
import { EventTypeTrendsChart } from '../../features/market-analytics/components/EventTypeTrendsChart'
import { LevelStatsCard, densityStatsOf } from '../../features/market-analytics/components/LevelStatsCard/LevelStatsCard'
import { LevelTrendsChart, LevelTrendsChartSplit } from '../../features/market-analytics/components/LevelTrendsChart/LevelTrendsChart'
import { MarketAnalyticsFilterBar } from '../../features/market-analytics/components/MarketAnalyticsFilterBar'
import { NewAdoptersModal } from '../../features/market-analytics/components/NewAdoptersModal'
import { TypicalDurationsCard } from '../../features/market-analytics/components/TypicalDurationsCard/TypicalDurationsCard'
import {
  buildEventCategoryPath,
  buildEventTypePath,
  familyEventTypeMemberships,
  filterCatalogueToScope,
  rollupRowAdoption,
  resolveCohortSize,
} from '../../features/market-analytics/components/helpers/catalogueUtils'
import { toDurationBuckets } from '../../features/market-analytics/components/helpers/durationBuckets'
import { getEventTypeDescription } from '../../features/market-analytics/components/helpers/eventTypeGlossary'
import { buildHierarchySiblings } from '../../features/market-analytics/components/helpers/hierarchySiblings'
import { toNewAdopterGames } from '../../features/market-analytics/components/helpers/newAdopterGames'
import { selectedPeriodFor } from '../../features/market-analytics/components/helpers/trendsSelectedPeriod'
import { useCatalogueScope, useCatalogueTaxonomyCounts } from '../../features/market-analytics/hooks/useCatalogueScope'
import { useNewAdopterGameIcons } from '../../features/market-analytics/hooks/useNewAdopterGameIcons'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import usePage from '../../hooks/usePage'
import languageService from '../../services/LanguageService'
import PageService from '../../services/PageService'
import { MarketEventCategoryDetailPageSkeleton } from './MarketEventCategoryDetailPageSkeleton'

/**
 * Event-category detail page: the top level of the hierarchy below the
 * catalogue. Shows the category's segment-scoped adoption stats from
 * `rollup?groupBy=EVENT_GROUP`, its trend as a single line (the event-group
 * trends endpoint aggregates per category server-side, so — unlike the
 * event-type page — no per-child approximation is needed), and the event types
 * it contains, each linking down to its own page.
 */
const MarketEventCategoryDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const { eventCategoryId } = useParams<{ eventCategoryId: string }>()
  const userLanguage = useCurrentUserLanguage()

  usePage(PageService.getPageWithId('market-analytics-event-category'), 'Visited Market Analytics Event Category Detail')

  // Same URL-backed filter/window state as the catalogue and the other detail
  // pages, so filters carried in on a crumb link parse natively here too.
  const {
    state,
    tagGroupInfos,
    windowDays,
    selectedCategoryIds,
    selectedGenreIds,
    selectedSubgenreIds,
    effectiveSubgenreIds,
    conventionalSubgenreIds,
    scope,
    isUnscoped,
    carriedSearch,
    rootCrumbSearch,
    crumbNavState,
  } = useCatalogueScope()

  // ---- Data ------------------------------------------------------------------
  // Catalogue list: the tag → tag-group mapping that says which event types
  // belong to this category, and the hierarchy bar's sibling inventory. Same
  // args as the other pages, so this hits the RTK cache.
  const {
    data: catalogueList,
    isLoading: isCatalogueLoading,
    isFetching: isCatalogueFetching,
    isError: isCatalogueError,
    refetch: refetchCatalogue,
  } = useGetBlueprintFamiliesQuery({ windowDays, ...scope })
  // Taxonomy picker counts, from the unscoped snapshot as on the catalogue page.
  const { allBlueprints, taxonomyCounts } = useCatalogueTaxonomyCounts(catalogueList, isUnscoped)

  // Per-category aggregates (games deduplicated across the category's blueprints
  // server-side) and the cohort size — both from the event-group rollup.
  const {
    currentData: groupRollup,
    isFetching: isGroupRollupFetching,
    isError: isGroupRollupError,
    refetch: refetchGroupRollup,
  } = useGetBlueprintFamilyRollupQuery({ groupBy: 'EVENT_GROUP', windowDays, ...scope })
  const categoryRow = useMemo(() => (groupRollup ?? []).find((row) => row.groupId === eventCategoryId), [groupRollup, eventCategoryId])
  const cohortSize = useMemo(() => resolveCohortSize(groupRollup ?? []), [groupRollup])
  // This page is about the category itself, so its tile counts every game running any
  // event of it — not only those whose blueprint was catalogued into a family.
  const categoryAdoption = useMemo(() => rollupRowAdoption(categoryRow), [categoryRow])

  // The games that FIRST ran an event of this category inside the window, named by the
  // backend, icons included; the tracked-games list fills in for backends predating GR-3245. A
  // category can list FEWER of them than one of its event types: "first event of the
  // category" is the harder bar to clear.
  const { iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId, isLoading: isGameIconsLoading } = useNewAdopterGameIcons()
  const newAdopterGames = useMemo(
    () => toNewAdopterGames(categoryRow?.newGames, { userLanguage, iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId }),
    [categoryRow?.newGames, userLanguage, iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId]
  )

  // Remembers which event category the modal was opened for, so moving to a sibling via the hierarchy bar
  // (same route, same component) doesn't carry an open modal over to the next one.
  const [newAdoptersOpenFor, setNewAdoptersOpenFor] = useState<string | null>(null)
  const isNewAdoptersModalOpen = Boolean(eventCategoryId) && newAdoptersOpenFor === eventCategoryId
  const openNewAdoptersModal = useCallback(() => setNewAdoptersOpenFor(eventCategoryId ?? null), [eventCategoryId])
  const closeNewAdoptersModal = useCallback(() => setNewAdoptersOpenFor(null), [])
  // The page stays mounted when only the id changes, so going on to another one and back must not reopen it.
  useEffect(() => {
    setNewAdoptersOpenFor(null)
  }, [eventCategoryId])

  // Per-event-type aggregates for the child list.
  const {
    currentData: typeRollup,
    isFetching: isTypeRollupFetching,
    isError: isTypeRollupError,
    refetch: refetchTypeRollup,
  } = useGetBlueprintFamilyRollupQuery({ groupBy: 'EVENT_TYPE', windowDays, ...scope })

  // The category's own trend series (games deduplicated across its blueprints
  // server-side). Feeds the chart's total line and the Density tiles.
  const {
    currentData: categoryTrendsItems,
    isFetching: isCategoryTrendsFetching,
    isError: isCategoryTrendsError,
    refetch: refetchCategoryTrends,
  } = useGetBlueprintFamilyEventGroupTrendsQuery({ eventGroupIds: eventCategoryId ? [eventCategoryId] : [], windowDays, ...scope }, { skip: !eventCategoryId })
  const categoryTrends = useMemo(() => (categoryTrendsItems ?? []).find((item) => item.id === eventCategoryId), [categoryTrendsItems, eventCategoryId])
  const { densityScalar } = useMemo(() => densityStatsOf(categoryTrends?.density), [categoryTrends?.density])

  // The months the chart plots: the selected period's, which is what `categoryTrends`
  // covers. The Density tile reads the same response.
  const selectedPeriod = useMemo(() => selectedPeriodFor(windowDays), [windowDays])

  // Typical durations, category-scoped: how long games run events of this category, across
  // every blueprint family under it. Summing the families' own histograms would count a
  // game once per family it adopted, so this waited for the grouped endpoint (handoff C3).
  const {
    currentData: durationItems,
    isFetching: isDurationFetching,
    isError: isDurationError,
    refetch: refetchDuration,
  } = useGetBlueprintFamilyEventGroupDurationDistributionQuery(
    { eventGroupIds: eventCategoryId ? [eventCategoryId] : [], windowDays, ...scope },
    { skip: !eventCategoryId }
  )
  const durationRow = useMemo(() => (durationItems ?? []).find((item) => item.id === eventCategoryId), [durationItems, eventCategoryId])
  const durationBuckets = useMemo(() => toDurationBuckets(durationRow?.buckets, t), [durationRow, t])

  // The Change tile. This used to difference the series above, which read too low
  // on every point but the last: each point is a trailing window, so the first one
  // is the window ending at the axis start, not the period before it. The momentum
  // endpoint's grouped mode measures it directly, over a cohort shared by both
  // windows and deduplicated across the category's blueprints.
  const {
    currentData: categoryMomentum,
    isFetching: isCategoryMomentumFetching,
    isError: isCategoryMomentumError,
    refetch: refetchCategoryMomentum,
  } = useGetBlueprintFamilyMomentumQuery({ groupBy: 'EVENT_GROUP', windowDays, ...scope }, { skip: !eventCategoryId })
  const categoryMomentumRow = useMemo(() => (categoryMomentum ?? []).find((row) => row.id === eventCategoryId), [categoryMomentum, eventCategoryId])
  const categoryDeltaGames = categoryMomentumRow?.momentum ?? null

  // ---- Derived display values --------------------------------------------------
  const categoryName = eventCategoryId ? languageService.getTranslation('tagGroups', eventCategoryId) || eventCategoryId : ''
  const categoryColorHex = useMemo(() => tagGroupInfos.find((group) => group.id === eventCategoryId)?.colorHex, [tagGroupInfos, eventCategoryId])

  useDocumentTitle(t('sidebar:market_analytics') + (categoryName ? `: ${categoryName}` : ''))

  // Event types under this category: the scoped list's memberships plus, while a segment
  // is active, the unscoped snapshot's, so a narrow segment filter can't make the
  // category look empty or "not found". The snapshot is the request the taxonomy counts
  // already make, so this adds none.
  // NOTE: open question for the backend — whether the list's `eventTypes` depend on the
  // segment at all. The snapshot is also on the default window rather than `windowDays`;
  // the union is never narrower than the scoped list, so either answer is safe here.
  const eventTypeIdsInCategory = useMemo(() => {
    const ids = new Set<string>()
    for (const list of [catalogueList, allBlueprints]) {
      for (const bp of list ?? []) {
        for (const membership of familyEventTypeMemberships(bp)) {
          if (membership.categoryId === eventCategoryId) ids.add(membership.typeId)
        }
      }
    }
    return ids
  }, [catalogueList, allBlueprints, eventCategoryId])

  // Segment-scoped inventory, matching how the other detail pages scope their menus.
  const scopedCatalogueList = useMemo(() => filterCatalogueToScope(catalogueList, effectiveSubgenreIds), [catalogueList, effectiveSubgenreIds])

  // Child event types, each with its rollup row's figures against the segment size.
  const typeRows = useMemo<EventCategoryTypeRow[]>(() => {
    return (typeRollup ?? [])
      .filter((row) => eventTypeIdsInCategory.has(row.groupId))
      .map((row) => {
        const adoptingGames = row.distinctGamesCount ?? null
        return {
          id: row.groupId,
          name: languageService.getTranslation('tags', row.groupId) || row.groupName || row.groupId,
          to: buildEventTypePath(row.groupId, carriedSearch),
          state: crumbNavState,
          adoptionPct: adoptingGames !== null && cohortSize && cohortSize > 0 ? adoptingGames / cohortSize : row.adoptionRate?.windowed ?? null,
          adoptingGames,
          newAdopters: row.newGames?.length ?? null,
          description: getEventTypeDescription(row.groupId) || undefined,
        }
      })
  }, [typeRollup, eventTypeIdsInCategory, carriedSearch, crumbNavState, cohortSize])

  // The split view's lines, most adopted first — the endpoint takes at most 10 ids and
  // the chart plots them in the order given, so the cut has to be deliberate rather
  // than whatever order the rollup came back in. Reuses the rows already built for the
  // table below, so the two always name the same types.
  const splitTypes = useMemo(
    () =>
      [...typeRows]
        .sort((a, b) => (b.adoptingGames ?? -1) - (a.adoptingGames ?? -1))
        .map((row) => ({ id: row.id, name: row.name, description: getEventTypeDescription(row.id) || undefined, isPlainTextDescription: true })),
    [typeRows]
  )

  // The chart plots this one category as a single, server-aggregated line. Gated on the
  // series, not just the id — see the event-type page: an omitted entity would otherwise
  // render as an empty total line rather than falling back to the split view. Memoized,
  // like `trendsSplit`, because both feed the memoized chart card.
  const trendsTotal = useMemo(
    () =>
      eventCategoryId && categoryTrends
        ? { item: { id: eventCategoryId, name: categoryName }, source: categoryTrends, period: selectedPeriod ?? undefined }
        : null,
    [eventCategoryId, categoryName, categoryTrends, selectedPeriod]
  )
  const isTrendsTotalLoading = isCategoryTrendsFetching && !categoryTrendsItems
  const isSplitLoading = isTypeRollupFetching && splitTypes.length === 0
  const splitLabel = t('market-analytics:trend_split_by_event_types')
  const trendsSplit = useMemo<LevelTrendsChartSplit>(
    () => ({
      label: splitLabel,
      render: ({ tabs, headerControls, footer, embedded }) => (
        <EventTypeTrendsChart
          types={splitTypes}
          metric={state.listSort}
          windowDays={windowDays}
          conventionalSubgenreIds={conventionalSubgenreIds}
          isLoading={isSplitLoading}
          tabs={tabs}
          headerControls={headerControls}
          footer={footer}
          hideSubtitle
          embedded={embedded}
        />
      ),
    }),
    [splitLabel, splitTypes, state.listSort, windowDays, conventionalSubgenreIds, isSplitLoading]
  )

  // Sibling switchers: categories move between category pages; the event-type
  // and blueprint levels — which this page can't name, since it sits above them —
  // are placeholders whose menus drop straight into one of the category's types.
  const hierarchyMenus = useMemo<BlueprintHierarchyMenus>(() => {
    const siblings = buildHierarchySiblings(scopedCatalogueList, { categoryId: eventCategoryId, userLanguage, tagGroups: tagGroupInfos })

    return {
      eventCategory: {
        ariaLabel: t('market-analytics:hierarchy_menu_event_categories'),
        options: siblings.eventCategories.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          isCurrent: sibling.id === eventCategoryId,
          to: buildEventCategoryPath(sibling.id, carriedSearch),
          state: crumbNavState,
          colorHex: sibling.colorHex,
        })),
      },
      eventType: {
        ariaLabel: t('market-analytics:hierarchy_menu_event_types'),
        options: siblings.eventTypes.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          // None of them is being viewed — this page sits one level above.
          isCurrent: false,
          to: buildEventTypePath(sibling.id, carriedSearch),
          state: crumbNavState,
          colorHex: sibling.colorHex,
        })),
      },
    }
  }, [scopedCatalogueList, eventCategoryId, userLanguage, carriedSearch, crumbNavState, tagGroupInfos, t])

  // A failed section request would otherwise read as a dash, a hidden card or an empty
  // table. Retry re-runs every request that failed, not just the one that surfaced it.
  const hasSectionLoadError = isGroupRollupError || isTypeRollupError || isCategoryTrendsError || isDurationError || isCategoryMomentumError
  const retryFailedRequests = useCallback(() => {
    if (isCatalogueError) refetchCatalogue()
    if (isGroupRollupError) refetchGroupRollup()
    if (isTypeRollupError) refetchTypeRollup()
    if (isCategoryTrendsError) refetchCategoryTrends()
    if (isDurationError) refetchDuration()
    if (isCategoryMomentumError) refetchCategoryMomentum()
  }, [
    isCatalogueError,
    refetchCatalogue,
    isGroupRollupError,
    refetchGroupRollup,
    isTypeRollupError,
    refetchTypeRollup,
    isCategoryTrendsError,
    refetchCategoryTrends,
    isDurationError,
    refetchDuration,
    isCategoryMomentumError,
    refetchCategoryMomentum,
  ])

  // Only the catalogue list gates the first render (the category's membership
  // comes from it); filter/window changes refetch in place with per-section loading.
  if (isCatalogueLoading) {
    return (
      <MarketEventCategoryDetailPageSkeleton
        selectedCategoryCount={selectedCategoryIds.length}
        selectedGenreCount={selectedGenreIds.length}
        selectedSubgenreCount={selectedSubgenreIds.length}
      />
    )
  }

  // A failed list leaves the category with no membership, which would read as "not
  // found" (or an empty table). `catalogueList` may still hold the previous segment's
  // list after a failed refetch, so the error is checked before anything reads it.
  if (isCatalogueError) {
    return (
      <DetailPageMessageCard
        title={t('market-analytics:catalogue_load_error')}
        description={t('market-analytics:catalogue_load_error_description')}
        backTo={`/live-event-analytics${rootCrumbSearch}`}
        onRetry={retryFailedRequests}
      />
    )
  }

  // An unknown tag-group id has neither a translated name nor blueprints anywhere
  // in the catalogue — the id is wrong (or the category was retired), so say so
  // instead of rendering an empty page.
  const isUnknownCategory = !eventCategoryId || (eventTypeIdsInCategory.size === 0 && !languageService.getTranslation('tagGroups', eventCategoryId))
  if (isUnknownCategory) {
    return (
      <DetailPageMessageCard
        title={t('market-analytics:event_category_not_found')}
        description={t('market-analytics:event_category_not_found_description', { id: eventCategoryId })}
        backTo={`/live-event-analytics${rootCrumbSearch}`}
      />
    )
  }

  return (
    <Box>
      {/* Hierarchy bar: back to catalog › this category › the event-type and
          blueprint levels as placeholders you can drop into */}
      <BlueprintHierarchyBar categoryName={categoryName} catalogueSearch={rootCrumbSearch} menus={hierarchyMenus} />

      {/* Segment filter: same cascading chips as the catalogue, in the same spot. */}
      <Box sx={{ mt: 1.5 }}>
        <MarketAnalyticsFilterBar
          selectedCategoryIds={selectedCategoryIds}
          selectedGenreIds={selectedGenreIds}
          selectedSubgenreIds={selectedSubgenreIds}
          onSetCategories={state.setCategories}
          onSetGenres={state.setGenres}
          onSetSubgenres={state.setSubgenres}
          onClearCategories={state.clearCategories}
          onClearGenres={state.clearGenres}
          onClearSubgenres={state.clearSubgenres}
          onClearTaxonomy={state.clearTaxonomy}
          taxonomyCounts={taxonomyCounts}
          cohortGamesCount={cohortSize}
        />
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h2" component="h1" sx={{ fontWeight: 'bold', lineHeight: 1.2, wordBreak: 'break-word' }}>
          {categoryName}
        </Typography>
      </Box>

      <Stack spacing={2} sx={{ minWidth: 0 }}>
        {hasSectionLoadError ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={retryFailedRequests}>
                {t('market-analytics:retry')}
              </Button>
            }
          >
            {t('market-analytics:detail_partial_load_error')}
          </Alert>
        ) : null}

        {/* Same card and tiles as the blueprint and event type pages. The total line is
            the category's own deduplicated series, and `trends/event-types` now splits it
            by the types it contains (handoff C1).

            The Adoption tile and the event-type table below it count games running any
            event of the category / type, the same rule as the trends series. */}
        <LevelStatsCard
          kind="eventCategory"
          window={state.window}
          onWindowChange={state.setWindow}
          adoptingGames={categoryAdoption.adoptingGames}
          cohortSize={cohortSize ?? categoryMomentumRow?.cohortGamesCount ?? null}
          adoptionRateFallback={categoryAdoption.adoptionRate}
          deltaGames={categoryDeltaGames}
          densityScalar={densityScalar}
          newAdopters={categoryRow?.newGames?.length ?? null}
          newAdopterGames={newAdopterGames}
          onNewAdoptersClick={openNewAdoptersModal}
          isAdoptionLoading={isGroupRollupFetching}
          isNewAdoptersLoading={(isGroupRollupFetching && !categoryRow) || isGameIconsLoading}
          isChangeLoading={isCategoryMomentumFetching && !categoryMomentumRow}
          isDensityLoading={isCategoryTrendsFetching && !categoryTrends}
          chart={
            <LevelTrendsChart
              metric={state.listSort}
              onMetricChange={state.setListSort}
              nameColumnHeader={t('market-analytics:event_category')}
              total={trendsTotal}
              isTotalLoading={isTrendsTotalLoading}
              isTotalError={isCategoryTrendsError && !categoryTrendsItems}
              onTotalRetry={refetchCategoryTrends}
              split={trendsSplit}
              cohortSize={cohortSize}
              embedded
            />
          }
        />

        {/* Category-scoped durations histogram; hidden when every bucket is empty,
            skeleton while the fetch resolves — same as on the blueprint page. */}
        <TypicalDurationsCard
          buckets={durationBuckets}
          description={t('market-analytics:durations_description_event_category')}
          isLoading={isDurationFetching && !durationRow}
        />

        <EventCategoryTypesCard
          rows={typeRows}
          isLoading={(isTypeRollupFetching || isCatalogueFetching) && typeRows.length === 0}
          categoryName={categoryName}
          categoryColorHex={categoryColorHex}
        />
      </Stack>
      <NewAdoptersModal
        open={isNewAdoptersModalOpen}
        onClose={closeNewAdoptersModal}
        games={newAdopterGames}
        isLoading={(isGroupRollupFetching && !categoryRow) || isGameIconsLoading}
        kind="eventCategory"
        entityName={categoryName}
        window={state.window}
      />
    </Box>
  )
}

export default MarketEventCategoryDetailPage
